import { Component, OnInit, OnDestroy, inject, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, Validators, ReactiveFormsModule, FormsModule, FormControl, FormArray } from '@angular/forms';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatTabsModule } from '@angular/material/tabs';
import { MatIconModule } from '@angular/material/icon';
import { MatRadioModule } from '@angular/material/radio';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatTableDataSource } from '@angular/material/table';
import { Country } from 'country-state-city';
import { Observable, of, Subject } from 'rxjs';
import { map, startWith, takeUntil, debounceTime } from 'rxjs/operators';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { CsvUploadDialogComponent } from '../../shared/components/csv-upload-dialog/csv-upload-dialog.component';

interface CompanyEntity {
  id: string;
  companyName: string;
  country: string;
  countryDisplay: string;
  address: string;
  companySize: string;
  market: string;
}

interface IndividualEntity {
  id: string;
  fullName: string;
  country: string;
  countryDisplay: string;
  address: string;
}

interface VesselEntity {
  id: string;
  name: string;
  imoNumber: string;
}

@Component({
  selector: 'app-search-form',
  templateUrl: './search-form.component.html',
  styleUrls: ['./search-form.component.scss'],
  imports: [
    MatFormFieldModule,
    MatButtonModule,
    MatSelectModule,
    ReactiveFormsModule,
    CommonModule,
    FormsModule,
    MatInputModule,
    MatButtonToggleModule,
    MatDialogModule,
    MatTabsModule,
    MatAutocompleteModule,
    MatIconModule,
    MatRadioModule,
    MatTableModule,
    MatTooltipModule,
    MatSnackBarModule
  ],
  standalone: true
})
export class SearchFormComponent implements OnInit, OnDestroy {
  dialog = inject(MatDialog);
  private snackBar = inject(MatSnackBar);
  private destroy$ = new Subject<void>();
  
  @Input() initialEntityType: 'Companies' | 'Individuals' | 'Vessels' = 'Companies';
  
  @Output() formSubmitted = new EventEmitter<any>();
  @Output() backToSearch = new EventEmitter<void>();
  
  searchBy: string = 'Companies';
  individualForm!: FormGroup;
  companyForm!: FormGroup;
  vesselForm!: FormGroup;
  
  // Bulk forms
  bulkCompanyForm!: FormGroup;
  bulkIndividualForm!: FormGroup;
  bulkVesselForm!: FormGroup;
  
  countries: any[] = [];
  filteredCountriesCompany: Observable<any[]>;
  filteredCountriesIndividual: Observable<any[]>;
  countryFilterCompany: FormControl;
  countryFilterIndividual: FormControl;
  
  // Validation and filtering
  showInvalidRowsOnly = false;
  filterState: 'all' | 'valid' | 'invalid' = 'all';
  validationSummary = { validRows: 0, invalidRows: 0, totalRows: 0 };
  
  // Table configurations
  companyDisplayedColumns: string[] = ['companyName', 'country', 'address', 'companySize', 'market', 'actions'];
  individualDisplayedColumns: string[] = ['fullName', 'country', 'address', 'actions'];
  vesselDisplayedColumns: string[] = ['name', 'imoNumber', 'actions'];
  
  // Data sources for tables
  companyDataSource = new MatTableDataSource<FormGroup>([]);
  individualDataSource = new MatTableDataSource<FormGroup>([]);
  vesselDataSource = new MatTableDataSource<FormGroup>([]);
  
  markets: string[] = [
    'Agriculture', 'Automotive', 'Aerospace', 'Banking and Finance', 'Biotechnology',
    'Construction', 'Consumer Goods', 'Defense', 'Education', 'Energy', 'Entertainment',
    'Environmental Services', 'Fashion', 'Food and Beverage', 'Healthcare', 'Hospitality',
    'Information Technology', 'Insurance', 'Legal Services', 'Logistics and Transportation',
    'Manufacturing', 'Media and Communications', 'Mining', 'Nonprofit and NGOs', 'Pharmaceuticals',
    'Professional Services', 'Real Estate', 'Retail', 'Telecommunications', 'Tourism and Travel',
    'Utilities', 'Wholesale Distribution', 'Other'
  ];

  constructor(private fb: FormBuilder) {
    this.countries = Country.getAllCountries();
    this.countryFilterCompany = new FormControl('');
    this.countryFilterIndividual = new FormControl('');
    this.filteredCountriesCompany = of(this.countries);
    this.filteredCountriesIndividual = of(this.countries);
  }

  ngOnInit(): void {
    this.searchBy = this.initialEntityType;
    this.initializeForms();
    this.initializeCountryFilters();
    this.initializeDataSources();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeForms(): void {
    // Single entity forms
    this.individualForm = this.fb.group({
      fullName: ['', Validators.required],
      address: [''],
      country: ['', Validators.required],
    });

    this.companyForm = this.fb.group({
      companyName: ['', Validators.required],
      address: [''],
      country: ['', Validators.required],
      companySize: [''],
      market: ['']
    });

    this.vesselForm = this.fb.group({
      name: ['', Validators.required],
      imoNumber: ['', [Validators.required, Validators.pattern(/^\d{7}$/)]]
    });

    // Bulk entity forms with FormArrays - initialize with one empty row
    this.bulkCompanyForm = this.fb.group({
      entities: this.fb.array([this.createCompanyFormGroup()])
    });

    this.bulkIndividualForm = this.fb.group({
      entities: this.fb.array([this.createIndividualFormGroup()])
    });

    this.bulkVesselForm = this.fb.group({
      entities: this.fb.array([this.createVesselFormGroup()])
    });
  }

  private initializeCountryFilters(): void {
    this.filteredCountriesCompany = this.countryFilterCompany.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountries(value || ''))
    );

    this.filteredCountriesIndividual = this.countryFilterIndividual.valueChanges.pipe(
      startWith(''),
      map(value => this._filterCountries(value || ''))
    );
  }

  // Form Array getters
  get companyEntities(): FormArray {
    return this.bulkCompanyForm.get('entities') as FormArray;
  }

  get individualEntities(): FormArray {
    return this.bulkIndividualForm.get('entities') as FormArray;
  }

  get vesselEntities(): FormArray {
    return this.bulkVesselForm.get('entities') as FormArray;
  }

  // Create form groups for entities
  private createCompanyFormGroup(entity?: Partial<CompanyEntity>): FormGroup {
    return this.fb.group({
      id: [entity?.id || this.generateId()],
      companyName: [entity?.companyName || '', Validators.required],
      country: [entity?.country || '', Validators.required],
      countryDisplay: [entity?.countryDisplay || ''],
      address: [entity?.address || ''],
      companySize: [entity?.companySize || ''],
      market: [entity?.market || '']
    });
  }

  private createIndividualFormGroup(entity?: Partial<IndividualEntity>): FormGroup {
    return this.fb.group({
      id: [entity?.id || this.generateId()],
      fullName: [entity?.fullName || '', Validators.required],
      country: [entity?.country || '', Validators.required],
      countryDisplay: [entity?.countryDisplay || ''],
      address: [entity?.address || '']
    });
  }

  private createVesselFormGroup(entity?: Partial<VesselEntity>): FormGroup {
    return this.fb.group({
      id: [entity?.id || this.generateId()],
      name: [entity?.name || '', Validators.required],
      imoNumber: [entity?.imoNumber || '', [Validators.required, Validators.pattern(/^\d{7}$/)]]
    });
  }

  private generateId(): string {
    return Math.random().toString(36).substr(2, 9);
  }

  // Add new entity methods
  addCompanyEntity(): void {
    const newEntity = this.createCompanyFormGroup();
    this.companyEntities.push(newEntity);
    this.updateCompanyDataSource();
  }

  addIndividualEntity(): void {
    const newEntity = this.createIndividualFormGroup();
    this.individualEntities.push(newEntity);
    this.updateIndividualDataSource();
  }

  addVesselEntity(): void {
    const newEntity = this.createVesselFormGroup();
    this.vesselEntities.push(newEntity);
    this.updateVesselDataSource();
  }

  // Remove entity methods
  removeCompanyEntity(index: number): void {
    if (this.companyEntities.length > 0) {
      this.companyEntities.removeAt(index);
      this.updateCompanyDataSource();
    }
  }

  removeIndividualEntity(index: number): void {
    if (this.individualEntities.length > 0) {
      this.individualEntities.removeAt(index);
      this.updateIndividualDataSource();
    }
  }

  removeVesselEntity(index: number): void {
    if (this.vesselEntities.length > 0) {
      this.vesselEntities.removeAt(index);
      this.updateVesselDataSource();
    }
  }

  // Clear all entities
  clearAllCompanies(): void {
    while (this.companyEntities.length > 1) {
      this.companyEntities.removeAt(this.companyEntities.length - 1);
    }
    // Reset the remaining row to empty values
    this.companyEntities.at(0).reset();
    this.updateCompanyDataSource();
    this.showSnackBar('All companies cleared');
  }

  clearAllIndividuals(): void {
    while (this.individualEntities.length > 1) {
      this.individualEntities.removeAt(this.individualEntities.length - 1);
    }
    // Reset the remaining row to empty values
    this.individualEntities.at(0).reset();
    this.updateIndividualDataSource();
    this.showSnackBar('All individuals cleared');
  }

  clearAllVessels(): void {
    while (this.vesselEntities.length > 1) {
      this.vesselEntities.removeAt(this.vesselEntities.length - 1);
    }
    // Reset the remaining row to empty values
    this.vesselEntities.at(0).reset();
    this.updateVesselDataSource();
    this.showSnackBar('All vessels cleared');
  }

  // Data source update methods
  private updateCompanyDataSource(): void {
    let data = this.companyEntities.controls as FormGroup[];
    
    // Apply filtering based on filter state
    if (this.filterState === 'valid') {
      data = data.filter(control => !this.hasRowValidationErrors(control));
    } else if (this.filterState === 'invalid') {
      data = data.filter(control => this.hasRowValidationErrors(control));
    }
    // If filterState is 'all', show all data without filtering
    
    this.companyDataSource.data = data;
    this.updateValidationSummary();
    
    // Subscribe to form changes for real-time validation updates
    this.subscribeToFormChanges();
  }

  private updateIndividualDataSource(): void {
    let data = this.individualEntities.controls as FormGroup[];
    
    // Apply filtering based on filter state
    if (this.filterState === 'valid') {
      data = data.filter(control => !this.hasRowValidationErrors(control));
    } else if (this.filterState === 'invalid') {
      data = data.filter(control => this.hasRowValidationErrors(control));
    }
    // If filterState is 'all', show all data without filtering
    
    this.individualDataSource.data = data;
    this.updateValidationSummary();
    
    // Subscribe to form changes for real-time validation updates
    this.subscribeToFormChanges();
  }

  private updateVesselDataSource(): void {
    let data = this.vesselEntities.controls as FormGroup[];
    
    // Apply filtering based on filter state
    if (this.filterState === 'valid') {
      data = data.filter(control => !this.hasRowValidationErrors(control));
    } else if (this.filterState === 'invalid') {
      data = data.filter(control => this.hasRowValidationErrors(control));
    }
    // If filterState is 'all', show all data without filtering
    
    this.vesselDataSource.data = data;
    this.updateValidationSummary();
    
    // Subscribe to form changes for real-time validation updates
    this.subscribeToFormChanges();
  }

  private subscribeToFormChanges(): void {
    const formArray = this.searchBy === 'Companies' ? this.companyEntities : 
                     this.searchBy === 'Individuals' ? this.individualEntities : this.vesselEntities;
    
    // Subscribe to the form array changes with debouncing to improve performance
    formArray.valueChanges
      .pipe(
        debounceTime(100), // Debounce rapid changes
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.updateValidationSummary();
      });
  }

  private initializeDataSources(): void {
    this.updateCompanyDataSource();
    this.updateIndividualDataSource();
    this.updateVesselDataSource();
  }

  // Country selection for bulk forms
  onBulkCountrySelection(country: any, formGroup: FormGroup): void {
    formGroup.patchValue({
      country: country.isoCode,
      countryDisplay: country.name
    });
  }

  getFilteredCountries(searchValue: string): any[] {
    return this._filterCountries(searchValue);
  }

  private _filterCountries(value: string): any[] {
    const filterValue = value.toLowerCase();
    return this.countries.filter(country => country.name.toLowerCase().includes(filterValue));
  }

  // Validation methods
  validateAllEntities(): boolean {
    return this.validationSummary.validRows > 0;
  }





  // Submit methods
  onSubmitBulk(): void {
    const formArray = this.searchBy === 'Companies' ? this.companyEntities : 
                     this.searchBy === 'Individuals' ? this.individualEntities : this.vesselEntities;
    
    // Get valid entities only
    const validEntities: any[] = [];
    const invalidCount = this.validationSummary.invalidRows;
    
    formArray.controls.forEach(control => {
      const formGroup = control as FormGroup;
      if (!this.hasRowValidationErrors(formGroup)) {
        validEntities.push(formGroup.value);
      }
    });

    if (validEntities.length === 0) {
      this.showSnackBar('No valid entities to submit. Please fix validation errors.', 'error');
      return;
    }



    // Show confirmation if there are invalid rows that will be skipped
    if (invalidCount > 0) {
      const proceed = confirm(
        `Submit ${validEntities.length} valid entities? ${invalidCount} invalid entities will be skipped.`
      );
      if (!proceed) return;
    }

    const formData = {
      type: this.searchBy === 'Companies' ? 'companies' : 
            this.searchBy === 'Individuals' ? 'individuals' : 'vessels',
      entities: validEntities,
      isBulk: true
    };

    this.formSubmitted.emit(formData);
  }

  // Existing single form methods remain the same
  onCountrySelectionCompany(country: any) {
    const countryControl = this.companyForm.get('country');
    if (countryControl) {
      countryControl.setValue(country.isoCode);
      this.countryFilterCompany.setValue(country.name);
    }
  }

  onCountrySelectionIndividual(country: any) {
    const countryControl = this.individualForm.get('country');
    if (countryControl) {
      countryControl.setValue(country.isoCode);
      this.countryFilterIndividual.setValue(country.name);
    }
  }

  onSubmitIndividual(): void {
    if (this.individualForm.valid) {
      const formData = {
        type: 'individual',
        fullName: this.individualForm.value.fullName,
        country: this.individualForm.value.country,
        address: this.individualForm.value.address,
        isBulk: false
      };
      this.formSubmitted.emit(formData);
      this.resetIndividual();
    }
  }

  onSubmitCompany(): void {
    if (this.companyForm.valid) {
      const formData = {
        type: 'company',
        companyName: this.companyForm.value.companyName,
        country: this.companyForm.value.country,
        address: this.companyForm.value.address,
        companySize: this.companyForm.value.companySize,
        market: this.companyForm.value.market,
        isBulk: false
      };
      this.formSubmitted.emit(formData);
      this.resetCompany();
    }
  }

  onSubmitVessel(): void {
    if (this.vesselForm.valid) {
      const formData = {
        type: 'vessels',
        entities: [this.vesselForm.value],
        isBulk: false
      };
      this.formSubmitted.emit(formData);
      this.resetVessel();
    }
  }

  resetIndividual(): void {
    this.individualForm.reset();
    this.countryFilterIndividual.setValue('');
  }

  resetCompany(): void {
    this.companyForm.reset();
    this.countryFilterCompany.setValue('');
  }

  resetVessel(): void {
    this.vesselForm.reset();
  }

  reset(): void {
    this.resetIndividual();
    this.resetCompany();
    this.resetVessel();
    this.updateDataSources();
  }

  private updateDataSources(): void {
    this.updateCompanyDataSource();
    this.updateIndividualDataSource();
    this.updateVesselDataSource();
  }

  // Enhanced validation methods for individual rows
  hasRowValidationErrors(formGroup: FormGroup): boolean {
    if (this.searchBy === 'Companies') {
      return this.isFieldInvalid(formGroup, 'companyName') ||
             this.isFieldInvalid(formGroup, 'address') ||
             this.isFieldInvalid(formGroup, 'country');
    } else if (this.searchBy === 'Individuals') {
      return this.isFieldInvalid(formGroup, 'fullName') ||
             this.isFieldInvalid(formGroup, 'country');
    } else { // Vessels
      return this.isFieldInvalid(formGroup, 'name') ||
             this.isFieldInvalid(formGroup, 'imoNumber');
    }
  }

  updateValidationSummary(): void {
    const formArray = this.searchBy === 'Companies' ? this.companyEntities : 
                     this.searchBy === 'Individuals' ? this.individualEntities : this.vesselEntities;
    let validRows = 0;
    let invalidRows = 0;

    formArray.controls.forEach(control => {
      if (this.hasRowValidationErrors(control as FormGroup)) {
        invalidRows++;
      } else {
        validRows++;
      }
    });

    this.validationSummary = {
      validRows,
      invalidRows,
      totalRows: formArray.length
    };
  }

  // Filter toggle methods
  toggleInvalidRowsFilter(): void {
    this.showInvalidRowsOnly = !this.showInvalidRowsOnly;
    this.updateDataSources();
  }

  showValidRows(): void {
    this.filterState = 'valid';
    this.updateDataSources();
  }

  showInvalidRows(): void {
    this.filterState = 'invalid';
    this.updateDataSources();
  }

  showAllRows(): void {
    this.filterState = 'all';
    this.updateDataSources();
  }



  // Utility methods
  private showSnackBar(message: string, type: 'success' | 'error' | 'info' = 'info'): void {
    this.snackBar.open(message, 'Close', {
      duration: 3000,
      panelClass: [`snackbar-${type}`]
    });
  }

  trackByIndex(index: number): number {
    return index;
  }

  // Get entity count for display
  getEntityCount(): number {
    return this.searchBy === 'Companies' ? this.companyEntities.length : this.individualEntities.length;
  }

  // CSV Upload functionality
  openCsvUploadDialog(): void {
    const dialogRef = this.dialog.open(CsvUploadDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      maxHeight: '90vh',
      data: {
        entityType: this.searchBy
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.entities) {
        this.importCsvEntities(result.entities);
      }
    });
  }

  private importCsvEntities(entities: any[]): void {
    // Clear existing entities
    if (this.searchBy === 'Companies') {
      this.companyEntities.clear();
    } else if (this.searchBy === 'Individuals') {
      this.individualEntities.clear();
    } else {
      this.vesselEntities.clear();
    }

    // Add imported entities
    entities.forEach(entity => {
      if (this.searchBy === 'Companies') {
        const formGroup = this.createCompanyFormGroup(entity);
        this.companyEntities.push(formGroup);
      } else if (this.searchBy === 'Individuals') {
        const formGroup = this.createIndividualFormGroup(entity);
        this.individualEntities.push(formGroup);
      } else {
        const formGroup = this.createVesselFormGroup(entity);
        this.vesselEntities.push(formGroup);
      }
    });

    // Update data sources (this will also update validation summary)
    this.updateDataSources();
    
    // Show feedback based on validation summary
    if (this.validationSummary.invalidRows > 0) {
      this.showSnackBar(
        `Imported ${entities.length} entities: ${this.validationSummary.validRows} valid, ${this.validationSummary.invalidRows} need fixes.`, 
        'error'
      );
    } else {
      this.showSnackBar(`Successfully imported ${entities.length} valid ${this.searchBy.toLowerCase()}`, 'success');
    }
  }



  /**
   * Validates if a country code/name is valid
   */
  isValidCountryCode(countryCode: string): boolean {
    if (!countryCode?.trim()) return false;
    
    // Check if it's a valid ISO code or country name
    return this.countries.some(country => 
      country.isoCode.toLowerCase() === countryCode.toLowerCase() ||
      country.name.toLowerCase() === countryCode.toLowerCase()
    );
  }

  /**
   * Checks if there are any validation errors in imported data
   */
  hasImportValidationErrors(): boolean {
    return this.validationSummary.invalidRows > 0;
  }

  /**
   * Checks if a specific field in a form group is invalid
   */
  isFieldInvalid(formGroup: FormGroup, fieldName: string): boolean {
    const control = formGroup.get(fieldName);
    if (!control) return false;

    const value = control.value;
    
    // Check based on field type and entity type
    switch (fieldName) {
      case 'companyName':
        return !value?.trim();
      case 'fullName':
        return !value?.trim();
      case 'name':
        // Vessel name validation
        return !value?.trim();
      case 'imoNumber':
        // IMO number validation: must be exactly 7 digits
        if (!value?.trim()) return true;
        const imoPattern = /^\d{7}$/;
        return !imoPattern.test(value.trim());
      case 'country':
        return !value?.trim() || !this.isValidCountryCode(value);
      case 'address':
        return !value?.trim();
      default:
        return false;
    }
  }
}